// Copyright (c) Ping. All rights reserved.
//
// Web host shim around the shared `<ConversationListPanel>` from
// `@ping/ui`. Subscribes to the Dexie-backed conversation list (via
// `useConversations`), owns the per-device FOLDER state (Dexie `folders` +
// `conversation_folder_mappings`, see core/persistence/spaces.ts), and threads
// selection / new-chat / folder callbacks through to the shared component.

import { useCallback, useEffect, useMemo, useState } from 'react';

import { liveQuery } from 'dexie';

import {
  type CompanyRow,
  type ConversationFilterTab,
  ConversationListPanel as ConversationListPanelUi,
  CreateFolderModal,
  useConversations,
} from '@ping/ui';

import { useCallStore } from '@calls/store/callStore';
import { archive, pin, unarchive, unpin } from '@core/persistence/conversations';
import { db } from '@core/persistence/db';
import {
  addConversationToFolder,
  createFolder,
  deleteFolder,
  removeConversationFromFolder,
} from '@core/persistence/spaces';
import { useMyCompanies } from '@features/business-directory';

interface ConversationListPanelProps {
  selectedConversationId: string | null;
  onConversationSelect: (id: string) => void;
  onNewChatPress: () => void;
  onNewGroupPress?: () => void;
  /** When supplied, the "+" Add menu shows a "Create new folder" row. The host
   *  also wires the list's folder controls regardless of this prop. */
  onCreateFolderPress?: () => void;
  /** Optional — when supplied, each row reveals a trash icon on hover
   *  and tapping it invokes this callback so the host can purge the
   *  conversation. */
  onConversationDelete?: (id: string) => void;
  /** Optional — when supplied, each row reveals a bell toggle on hover to
   *  mute/unmute the conversation's notifications. */
  onConversationMuteToggle?: (id: string, muted: boolean) => void;
  /** Optional — navigate to the Business Directory when the user selects a company. */
  onCompanyPress?: (id: string) => void;
  /** Optional — navigate to the Create Company flow. */
  onCreateCompany?: () => void;
  /** Optional — open the Activity feed from the header bell. */
  onNotificationsPress?: () => void;
  /** Optional — unread-activity count shown on the header bell. */
  notificationsBadgeCount?: number;
  /** Optional — suppress the panel's own resize handle (host renders one). */
  hideResizeHandle?: boolean;
  /** Optional — fill the parent's width instead of self-sizing (TabView scene). */
  fillWidth?: boolean;
}

const BUILT_IN_TABS: readonly ConversationFilterTab[] = [
  { id: 'all', label: 'All', leadingIcon: 'all' },
  { id: 'unread', label: 'Unread' },
  { id: 'favourites', label: 'Favourites', leadingIcon: 'star' },
  { id: 'dm', label: 'DM' },
];

export function ConversationListPanel({
  selectedConversationId,
  onConversationSelect,
  onNewChatPress,
  onNewGroupPress,
  onCreateFolderPress,
  onConversationDelete,
  onConversationMuteToggle,
  onCompanyPress,
  onCreateCompany,
  onNotificationsPress,
  notificationsBadgeCount,
  hideResizeHandle,
  fillWidth,
}: ConversationListPanelProps) {
  const { conversations, isLoading, error } = useConversations();

  // Offline-first companies — liveQuery-backed; re-renders on every Dexie write
  // (including optimistic writes from CreateCompanyPage).
  const { companies: myCompanies } = useMyCompanies();
  const companies = useMemo<readonly CompanyRow[]>(
    () =>
      myCompanies.map((c) => ({
        id: c.id,
        name: c.name,
        ...(c.logoUrl != null ? { avatarUri: c.logoUrl } : {}),
      })),
    [myCompanies],
  );

  // Reactive folder state: folder list (for the chips/picker) + the
  // conversation→folderIds membership map (for filtering + picker checkmarks).
  const [folders, setFolders] = useState<ReadonlyArray<{ id: string; name: string }>>([]);
  const [conversationFolders, setConversationFolders] = useState<
    Record<string, readonly string[]>
  >({});
  useEffect(() => {
    const sub = liveQuery(async () => {
      const fs = await db.folders.orderBy('order').toArray();
      const maps = await db.conversation_folder_mappings.toArray();
      return { fs, maps };
    }).subscribe({
      next: ({ fs, maps }) => {
        setFolders(fs.map((f) => ({ id: f.id, name: f.name })));
        const m: Record<string, string[]> = {};
        for (const mp of maps) {
          (m[mp.conversationId] ??= []).push(mp.folderId);
        }
        setConversationFolders(m);
      },
    });
    return () => sub.unsubscribe();
  }, []);

  // Sort by lastMessage timestamp DESC (most recent first). We NO LONGER drop
  // rows by empty name — that silently hid real joined groups whose title
  // hadn't hydrated yet ("missing chats"). Non-chats (device/call groups) are
  // suppressed explicitly in the read-model adapter, and every surviving row is
  // given a derived fallback title there (`deriveConversationTitle`), so a
  // name-less row can no longer reach the list.
  const sorted = useMemo(() => {
    const copy = conversations.slice();
    copy.sort((a, b) => {
      // Fall back to `createdAt` so a brand-new / just-joined chat with no
      // message yet sorts near the top (by creation) instead of at time 0
      // (bottom), where it reads as a "missing" chat. Mirrors the adapter sort.
      const ta = Math.max(a.lastMessage?.timestamp ?? 0, a.createdAt ?? 0);
      const tb = Math.max(b.lastMessage?.timestamp ?? 0, b.createdAt ?? 0);
      return tb - ta;
    });
    return copy;
  }, [conversations]);

  // Active call indicator — merge live call state into the matching row.
  // Returns primitives so the selector never creates new objects (avoids
  // the useSyncExternalStore infinite-loop pitfall). Also scans s.rings
  // so the receiver sees the badge while the IncomingCallBanner is ringing
  // (s.active is only set after the call is accepted/started).
  const activeCallConversationId = useCallStore((s) => {
    if (s.active?.conversationId) return s.active.conversationId;
    for (const [, ring] of s.rings) {
      if (ring.status === 'ringing') return ring.conversationId;
    }
    return null;
  });
  const activeCallMedia = useCallStore((s) => {
    if (s.active?.media) return s.active.media;
    for (const [, ring] of s.rings) {
      if (ring.status === 'ringing') return ring.media;
    }
    return null;
  });
  const sortedWithCalls = useMemo(() => {
    if (activeCallConversationId === null) return sorted;
    return sorted.map((c) =>
      c.id === activeCallConversationId
        ? { ...c, activeCall: { media: activeCallMedia as 'audio' | 'video' } }
        : c,
    );
  }, [sorted, activeCallConversationId, activeCallMedia]);

  const filterTabs = useMemo<readonly ConversationFilterTab[]>(
    () => [
      ...BUILT_IN_TABS,
      ...folders.map((f): ConversationFilterTab => ({
        id: f.id,
        label: f.name,
        leadingIcon: 'folder',
      })),
    ],
    [folders],
  );

  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const handleOpenCreateFolder = useCallback(() => {
    setCreateFolderOpen(true);
    onCreateFolderPress?.();
  }, [onCreateFolderPress]);
  const handleCreateFolder = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (trimmed.length === 0) return;
      void createFolder({
        id: `folder_${Date.now().toString(36)}${Math.floor(performance.now()).toString(36)}`,
        name: trimmed,
        order: folders.length,
      });
    },
    [folders.length],
  );

  const handleDeleteFolder = useCallback((folderId: string) => {
    void deleteFolder(folderId);
  }, []);

  // Archive / unarchive a conversation. Both helpers persist the `archivedAt`
  // marker to Dexie AND broadcast the device-group `conv_state` patch, so the
  // change mirrors to the user's other devices (multi-device sync).
  const handleArchiveToggle = useCallback(
    (conversationId: string, nextArchived: boolean) => {
      void (nextArchived ? archive(conversationId) : unarchive(conversationId));
    },
    [],
  );

  // Pin / unpin a conversation. Persists the `pinnedAt` marker + broadcasts the
  // device-group `conv_state` patch (multi-device sync), mirroring archive.
  const handlePinToggle = useCallback(
    (conversationId: string, nextPinned: boolean) => {
      void (nextPinned ? pin(conversationId) : unpin(conversationId));
    },
    [],
  );

  const handleToggleConversationFolder = useCallback(
    (conversationId: string, folderId: string) => {
      const inFolder = conversationFolders[conversationId]?.includes(folderId) ?? false;
      void (inFolder
        ? removeConversationFromFolder(conversationId, folderId)
        : addConversationToFolder(conversationId, folderId));
    },
    [conversationFolders],
  );

  return (
    <>
      <ConversationListPanelUi
        conversations={sortedWithCalls}
        selectedConversationId={selectedConversationId}
        isLoading={isLoading}
        error={error !== null ? error.message : null}
        onConversationPress={onConversationSelect}
        onNewChatPress={onNewChatPress}
        filterTabs={filterTabs}
        conversationFolders={conversationFolders}
        folders={folders}
        onCreateFolderPress={handleOpenCreateFolder}
        onDeleteFolder={handleDeleteFolder}
        onToggleConversationFolder={handleToggleConversationFolder}
        {...(onNewGroupPress !== undefined ? { onNewGroupPress } : {})}
        {...(onConversationDelete !== undefined ? { onConversationDelete } : {})}
        {...(onConversationMuteToggle !== undefined ? { onConversationMuteToggle } : {})}
        onConversationArchiveToggle={handleArchiveToggle}
        onConversationPinToggle={handlePinToggle}
        {...(companies.length > 0 ? { companies } : {})}
        {...(onCompanyPress !== undefined ? { onCompanyPress } : {})}
        {...(onCreateCompany !== undefined ? { onCreateCompany } : {})}
        {...(onNotificationsPress !== undefined ? { onNotificationsPress } : {})}
        {...(notificationsBadgeCount !== undefined ? { notificationsBadgeCount } : {})}
        {...(hideResizeHandle !== undefined ? { hideResizeHandle } : {})}
        {...(fillWidth !== undefined ? { fillWidth } : {})}
      />
      <CreateFolderModal
        open={createFolderOpen}
        onDismiss={() => setCreateFolderOpen(false)}
        onCreate={handleCreateFolder}
      />
    </>
  );
}
